import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { EmptyValue } from '@/components/common/EmptyValue';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const VALUE_CLASS = 'mt-0.5 text-sm font-semibold wrap-break-word';

export interface DetailItemProps {
  label: string;
  value: string | null | undefined;
  icon: LucideIcon;
  tooltip: ReactNode;
  isLoading?: boolean | undefined;
}

interface DetailValueProps {
  value: string | null | undefined;
  tooltip: ReactNode;
}

function DetailValue({ value, tooltip }: DetailValueProps) {
  if (value === null || value === undefined) {
    return (
      <p className={VALUE_CLASS}>
        <EmptyValue />
      </p>
    );
  }

  if (!tooltip || value === '') return <p className={VALUE_CLASS}>{value}</p>;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <p
            tabIndex={0}
            className={cn(
              VALUE_CLASS,
              'cursor-help rounded underline decoration-dotted focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none',
            )}
          >
            {value}
          </p>
        }
      />
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export function DetailItem({ label, value, icon: Icon, tooltip, isLoading }: DetailItemProps) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-muted/40 p-3 ring-1 ring-border/60 transition-colors ring-inset hover:bg-muted/70">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-card text-primary shadow-xs ring-1 ring-border/60">
        <Icon className="size-4" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-2xs font-medium tracking-wider text-muted-foreground uppercase">
          {label}
        </p>
        {isLoading ? (
          <Skeleton className="mt-1 h-4 w-28 max-w-full" />
        ) : (
          <DetailValue value={value} tooltip={tooltip} />
        )}
      </div>
    </div>
  );
}
